/**
 * Analytics per-page access model (hub-gated, v1).
 *
 * Two roles on top of `tools.analytics.enabled`:
 *  - ADMIN  (`tools.analytics.role` ∈ {admin, approver_admin}, or app admin) —
 *    sees every page, configures things (Reporting Center, the Admin area), and
 *    grants page access to viewers.
 *  - VIEWER (enabled, not admin) — sees ONLY the pages explicitly granted in
 *    `user.analyticsPages` (deny-by-default; nothing until granted).
 *
 * CONVENTION: every provisionable sub-nav page has a stable `key` listed here.
 * When a new analytics sub-nav is added, add its key to ANALYTICS_PAGES so it
 * participates in provisioning by default (registry entries carry the same key).
 *
 * Enforcement is CLIENT-SIDE in the hub (nav + route guards); the analytics API
 * still requires the base `analytics` custom claim to return any data. Harden to
 * server-enforced later if needed (grants would travel in the token claim).
 */
/** The provisionable analytics pages (NOT the admin area — that's admin-only). */
export const ANALYTICS_PAGES = [
    { key: 'traffic', label: 'Traffic' },
    { key: 'channels', label: 'Channels' },
    { key: 'competitors', label: 'Competitors radar' },
    { key: 'catalogue', label: 'Catalogue' },
    { key: 'articles', label: 'Articles' },
    { key: 'socialMedia', label: 'Social Media' },
    { key: 'newsletter', label: 'Newsletter' },
    { key: 'projects', label: 'Projects' },
    { key: 'membership', label: 'Memberships' },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'userInsights', label: 'User insights' },
    { key: 'ask', label: 'Ask Data Questions' },
    { key: 'reports', label: 'Reporting Center' },
    { key: 'psm', label: 'PSM' },
    // Own-view variant (USL bd_viewer pattern): the caller sees ONLY their own
    // PSM deep dive — clickupPsmGet filters the snapshot server-side.
    { key: 'psmOwn', label: 'PSM — own view' },
    { key: 'engagementLab', label: 'Engagement Lab' },
    { key: 'poPm', label: 'PO/PM' },
    // Business view of the designboom PayPal mirror (the Operations tool keeps
    // the accounting worklist; this grant is read-only revenue). The key ALSO
    // gates the /paypalIpn Firestore read in firestore.rules (analyticsPages
    // travels in the token claim) — keep the two in sync.
    { key: 'designboomRevenue', label: 'db User Submission Revenue' },
    // Capability key (no nav item, like psmOwn): may SET the end-of-year goals
    // on the db User Submission Revenue dashboard (paypalRevenueGoalsSet).
    // Viewing the dashboard still needs 'designboomRevenue'.
    { key: 'designboomRevenueGoals', label: 'db User Submission Revenue — goals editor' },
];
export const ANALYTICS_PAGE_KEYS = ANALYTICS_PAGES.map((p) => p.key);
/** Analytics admin = app admin OR an admin-flavored tool role. */
export function isAnalyticsAdmin(opts) {
    return opts.isAppAdmin || opts.role === 'admin' || opts.role === 'approver_admin';
}
/** Can this user see a given page? Admins → all; viewers → granted only. */
export function canViewAnalyticsPage(admin, granted, key) {
    if (admin)
        return true;
    return (granted ?? []).includes(key);
}
/** The page keys this user can see (admins = all). */
export function visibleAnalyticsPages(admin, granted) {
    return admin ? ANALYTICS_PAGE_KEYS : (granted ?? []).filter((k) => ANALYTICS_PAGE_KEYS.includes(k));
}
//# sourceMappingURL=analyticsPages.js.map